import {Auth0Provider, useAuth0} from "@auth0/auth0-react";
import {PropsWithChildren} from "react";
import {Outlet} from "react-router-dom";
import {Auth0Bridge} from "../Auth0Bridge/Auth0Bridge";
import {BootstrapGate} from "../BootstrapGate/BootstrapGate";
import {getAuthToken, getAuthUser} from "../../store/auth/auth-store";
import {AuthProvider} from "@ots-frontend/ots-ui";
import {DRIVER_ACTIVITY_PERMISSIONS} from "@ots-frontend/ots-ui-tw";
import {personType, personTypes} from "@ots-frontend/ots-data";

export const DispatchGoAuthProvider = () => {

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: 'openid profile email',
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <Auth0Bridge>
        <BootstrapGate>
          <AppAuthProvider>
            <Outlet/>
          </AppAuthProvider>
        </BootstrapGate>
      </Auth0Bridge>
    </Auth0Provider>
  );
}

/**
 * `AuthProvider`, told whether this session came through single sign-on so the profile can say the
 * password is changed there. It sits inside `Auth0Provider`, which is the only place that can tell.
 */
const AppAuthProvider = ({children}: PropsWithChildren) => {
  const {isAuthenticated} = useAuth0();

  return (
    <AuthProvider
      getAuthToken={getAuthToken}
      getAuthUser={getAuthUser}
      generatePermissions={permissionGenerator}
      ssoAuthenticated={isAuthenticated}>
      {children}
    </AuthProvider>
  );
}

const permissionGenerator = (userType: string | undefined): string[] => {
  if (!userType || userType === 'RESTRICTED') {
    return ["view"]
  }

  const permissions = [
    "view",
    "edit",
    "delete",

    "dispatch_delete",
    "dispatch_view",
    "dispatch_create",
    "dispatch_edit",

    "dispatchrequest_view",

    "dispatch_request_accept_reject",

    "dvir_create",
    "dvir_edit",
    "dvir_view",
    "dvir_delete",

    "shift_view",
    "shift_delete",
    "shift_edit",
    "shift_create",

    "transport_create",
    "transport_delete",
    "transport_edit",

    "transporttransportassoc_create",
    "transporttransportassoc_view",
    "transporttransportassoc_edit",
    "transporttransportassoc_delete",

    "view_cost",
    "restriction_view",
    "meter_view",

    "person_create",
    "person_delete",
    "person_edit",
    "person_view",

    "trailer_create",
    "trailer_edit",
    "trailer_view",

    "vehicle_create",
    "vehicle_edit",
    "vehicle_view",

    "geofence_view",
    "geofencesitegroup_view",

    "containerCluster_view",

    "container_view",

    "route_view",
    "site_view",
    "site_assignment_view",
    "site_assignment_site_view",
    "well_view"
  ]

  if (userType === 'ADMIN') {
    // WaterGo's admin-only Driver Activity tools: merging and splitting, and well allocation.
    permissions.push(DRIVER_ACTIVITY_PERMISSIONS.restructure)
    permissions.push(DRIVER_ACTIVITY_PERMISSIONS.wellAllocation)
    permissions.push('region_edit')
    permissions.push('view_tools')
    permissions.push('view_system_process_executions')
    permissions.push('object_mapping_view')
    permissions.push('object_mapping_edit')
    permissions.push('object_mapping_create')
    permissions.push('object_mapping_delete')
    permissions.push('meter_create')
    permissions.push("meter_edit")
    permissions.push("change_log_view")
    permissions.push("restriction_create")
    permissions.push("restriction_edit")
    permissions.push("containerCluster_edit")
    permissions.push("containerCluster_delete")

    for (const type of personTypes) {
      permissions.push(`person_${type.code.toLowerCase()}_create`)
      permissions.push(`person_${type.code.toLowerCase()}_delete`)
      permissions.push(`person_${type.code.toLowerCase()}_view`)
      permissions.push(`person_${type.code.toLowerCase()}_edit`)
    }
  }

  if (userType === 'USER') {
    for (const type of personTypes) {
      permissions.push(`person_${type.code.toLowerCase()}_view`)
      if (type !== personType.user && type !== personType.admin) {
        permissions.push(`person_${type.code.toLowerCase()}_create`)
        permissions.push(`person_${type.code.toLowerCase()}_delete`)
        permissions.push(`person_${type.code.toLowerCase()}_edit`)
      }
    }
  }

  return permissions
}
